import { useState } from 'react';
import { FilterList } from 'src/components/filterList/FilterList';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import MainPane from 'src/components/TwoPaneLayout/MainPane';
import SidePane from 'src/components/TwoPaneLayout/SidePane';
import TwoPaneLayout from 'src/components/TwoPaneLayout/TwoPaneLayout';
import {
  INoScreens,
  IOrientation,
  IOrientationLabel,
  MAX_ROWS_PER_PAGE,
} from 'src/constants';
import { useFilterAndSorting } from 'src/hooks';
import { IFilter } from 'src/interfaces';
import { escapeSearchTerms, generateOptions, lang } from 'src/utils';

import { useLocationGroupTab } from '../../hooks/useLocationGroupTab.hook';
import { LocationGroupRowActions } from './LocationGroupRowActions/LocationGroupRowActions';

export const LocationGroupTab = () => {
  const {
    onChangeParams,
    onSortChangeHandle,
    selectedSortOption,
    locationGroups,
    isLoadingLayoutTypes,
    isLoadingLocationGroups,
    layoutTypes,
    totalCount,
    totalPages,
    page,
    handlePageChange,
    activeFilters,
  } = useLocationGroupTab();

  const filters: IFilter[] = [
    {
      label: lang.channelType,
      options: generateOptions(layoutTypes, 'name', 'id'),
      isLoading: isLoadingLayoutTypes,
    },
    {
      label: lang.numberOfScreens,
      options: Object.values(INoScreens).map((value) => ({
        value: value,
        label: value,
      })),
    },
    {
      label: lang.screenOrientation,
      options: Object.values(IOrientation).map((value) => ({
        value: value,
        label: IOrientationLabel[`${value}`],
      })),
    },
  ];

  const [isFuzzySearch, setIsFuzzySearch] = useState(
    !('name.list' in activeFilters),
  );
  const {
    onChangeKeyword,
    onRemoveKeyword,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
    onReset,
    getParamsValue,
  } = useFilterAndSorting(filters, [], isFuzzySearch);

  const onResetFilters = () => {
    onReset();
    setIsFuzzySearch(true);
  };

  const onSearchTypeChange = () => {
    if (isFuzzySearch) {
      onChangeToExactMatch('name.regexp', 'name.list');
      setIsFuzzySearch(false);
    } else {
      onChangeToFuzzyMatch('name.list', 'name.regexp');
      setIsFuzzySearch(true);
    }
  };

  const onHandleChangeKeyword = (value: string | string[]) => {
    if (!isFuzzySearch) {
      onChangeKeyword(value);

      return;
    }

    // CC-21081: This is catching the Backslash event somehow, this verification allows us to avoid the remove of the search badge when pressing Backslash key
    if (value && value.length) {
      const escaped = escapeSearchTerms(value, getParamsValue('name.regexp'));
      // we should avoid the terms re-escaping
      onChangeParams('name.regexp', [...new Set(escaped)] as string[]);
    }
  };

  return (
    <>
      <TwoPaneLayout>
        <SidePane>
          <FilterList
            filters={filters}
            onChangeFilter={onChangeParams}
            onRemoveKeyword={onRemoveKeyword}
            onChangeKeyword={onHandleChangeKeyword}
            onReset={onResetFilters}
            showReset
            hasExactSearch
            onSearchTypeChange={onSearchTypeChange}
            isFuzzySearch={isFuzzySearch}
          />
        </SidePane>

        <MainPane>
          <CustomTableWithPagination
            columns={[
              { id: 'name', label: lang.locationGroupTab.groupName },
              {
                id: 'locations_count',
                label: lang.locationGroupTab.noOfLocations,
              },
              { id: 'layoutType', label: lang.locationGroupTab.channel },
              { id: 'actions', label: '' },
            ]}
            onSortChange={onSortChangeHandle}
            selectedSortOption={selectedSortOption}
            rows={locationGroups}
            loading={isLoadingLocationGroups}
            renderActions={(row) => <LocationGroupRowActions row={row} />}
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            rowsPerPage={MAX_ROWS_PER_PAGE}
            showTableControls
            onPageChange={handlePageChange}
          />
        </MainPane>
      </TwoPaneLayout>
    </>
  );
};
